import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ChevronsUpDown, Check, Loader2, Crown, Plus, Lock, Building2 } from "lucide-react";
import { useFeatureFlag, FLAG } from "@/lib/features";
import NewWorkspaceDialog from "@/components/NewWorkspaceDialog";
import { useEntitlements } from "@/lib/useEntitlements";
import { GateNeutralButton } from "@/components/UpgradeLock";

const ROLE_STYLES = {
  owner: "bg-amber-50 text-amber-700 border-amber-200",
  admin: "bg-lm-tint2 text-lm-primary border-lm-primary/20",
  member: "bg-lm-tint text-lm-text2 border-lm-border",
};

/**
 * Header workspace label + switcher. The dropdown is PORTALED to document.body
 * with a full-screen backdrop so it escapes the sticky-header stacking context —
 * a click anywhere outside (navbar included) closes it, guaranteed.
 */
export default function WorkspaceSwitcher({ current, status = "ready" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [switching, setSwitching] = useState(null);
  const [coords, setCoords] = useState(null);
  const [creating, setCreating] = useState(false);
  const triggerRef = useRef(null);
  const navigate = useNavigate();
  const {
    switchWorkspace,
    workspaceEpoch,
  } = useAuth();
  // Three states, never two. The gate booleans fail CLOSED, so before
  // entitlements land they are false — which is why the render below branches on
  // `entPending` / `entUnknown` FIRST and only then on the boolean. Reading the
  // boolean alone would show a paying customer a lock on every page load, which
  // is the exact defect the pending vocabulary exists to stop.
  const {
    loaded: entLoaded,
    pending: entPending,
    unknown: entUnknown,
  } = useEntitlements();

  // Phase 3.2 — the "New workspace" action is ABSENT when the flag is off, not
  // disabled. A disabled control advertises a feature that does not exist and
  // invites a support question we have no good answer to. `useFeatureFlag` fails
  // closed, so an unanswered or failed /features request also renders nothing.
  //
  // The AT-CAP case is deliberately the opposite: the action stays visible and
  // the 409 explains it. An invisible limit is worse than a refused click —
  // the user can see the ceiling exists and what it is.
  const { enabled: canCreate } = useFeatureFlag(FLAG.WORKSPACE_CREATION);
  // Set when a create returns 403 feature_disabled: the flag was flipped off
  // while this page was open, and the stale `true` we booted with must not keep
  // offering the action.
  const [flagRevoked, setFlagRevoked] = useState(false);
  const showCreate = canCreate && !flagRevoked;

  const place = useCallback(() => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) setCoords({ top: r.bottom + 8, left: r.left, width: r.width });
  }, []);

  const openMenu = () => { place(); setOpen(true); };

  // Reposition on scroll/resize while open; Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/workspaces/mine");
        setItems(r.data?.items || []);
      } catch (e) {
        // NEVER put the server's own text on a screen. `detail` is written for
        // an engineer reading a log, it is not keyed to any state we control,
        // and it has leaked stack-ish strings into a customer's face before.
        // Real error → console; user → our words.
        // eslint-disable-next-line no-console
        console.error("[workspaces/mine] failed", e?.response?.status, e?.response?.data?.detail);
        toast.error("Could not load workspaces.");
        setItems([]);
      }
    })();
    // Re-keyed on the epoch as well as `open`: `is_active` in this list is a
    // fact about the workspace we are in, so a switch invalidates it. Without
    // this the checkmark stays on the workspace the user just left until the
    // menu is closed and reopened.
  }, [open, workspaceEpoch]);

  // Phase 5.9 — the full page reload is GONE.
  //
  // It lived here, `window.location.assign("/app/dashboard")`, and it was not a
  // nicety: it was the only thing keeping the app correct. Roughly a dozen
  // screens fetched workspace-scoped data in a mount-time effect with `[]` deps,
  // and nothing told them the tenant had changed. Reloading remounted every one
  // of them, which made staleness structurally impossible — at the cost of
  // throwing the whole application away on a routine action.
  //
  // What replaces it, and why it is now safe to delete:
  //
  //   · `switchWorkspace` (AuthContext) clears the seat before re-deriving, so
  //     there is a window with NO tenant rather than a swap between two.
  //   · `workspaceEpoch` re-runs every workspace-scoped effect — the same job
  //     the remount did, without the remount.
  //   · every long poll captures the epoch and bails when it changes, which the
  //     reload achieved by destroying the JS context outright.
  //   · `X-Workspace-Id` makes each request state its tenant, so anything this
  //     list missed fails LOUDLY with a 409 instead of silently reading the
  //     wrong workspace.
  //
  // Do not reintroduce a reload here or anywhere else. A second one at
  // PaywallModal.jsx:80 was removed for the same reason: a reload in any path
  // masks exactly the epoch bugs this design exists to surface.
  //
  // Navigation stays: the user asked for a different workspace, and dropping
  // them on its dashboard is the honest landing. It is a soft navigate now, so
  // AuthContext state survives it.
  const switchTo = async (ws) => {
    if (ws.is_active || switching) return;
    setSwitching(ws.workspace_id);
    try {
      await switchWorkspace(ws.workspace_id);
      setOpen(false);
      toast.success(`Switched to ${ws.name}`);
      navigate("/app/dashboard");
    } catch (e) {
      // Same rule as above — our words, never the server's.
      // eslint-disable-next-line no-console
      console.error("[workspaces/switch] failed", e?.response?.status, e?.response?.data?.detail);
      toast.error("Couldn't switch workspace. Try again.");
    } finally {
      setSwitching(null);
    }
  };


  // §2/§4 — the name slot's job in each state. It carries NO error message; it
  // just stops claiming things it can't back up. Geometry is identical in every
  // branch, so nothing in the top bar shifts as state changes.
  //
  //   settling  fixed-width neutral block, no text, no spinner. Below 600ms
  //             nothing registers, and a spinner here manufactures a wait that
  //             didn't exist.
  //   loading   shimmer bar — legitimately loading, and says so by moving.
  //   retrying  shimmer STOPS and freezes neutral. Movement implies progress;
  //   /unreach  past the honesty line there is none to imply. Name = em-dash.
  //   ready     the real name.
  const hasName = !!current?.name;
  const settling = !hasName && status === "settling";
  const shimmering = !hasName && status === "loading";
  const stalled = !hasName && (status === "retrying" || status === "unreachable");

  const initial = hasName ? current.name.trim().charAt(0).toUpperCase() : "";

  return (
    <div className="relative min-w-0">
      {/* Trigger */}
      <button
        ref={triggerRef}
        type="button"
        data-testid="workspace-switcher-trigger"
        onClick={() => (open ? setOpen(false) : openMenu())}
        aria-expanded={open}
        className={`group flex items-center gap-2.5 min-w-0 max-w-full text-left rounded-lg px-2 py-1.5 -ml-1 border transition-colors ${
          open ? "bg-lm-tint/70 border-lm-border" : "border-transparent hover:bg-lm-tint/60"
        }`}
      >
        <span
          className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 font-display font-bold text-sm ${
            hasName ? "bg-lm-primary text-white shadow-sm" : "bg-lm-tint border border-lm-border"
          }`}
        >
          {initial}
        </span>
        <span className="min-w-0">
          <span
            data-testid="workspace-switcher-name"
            data-ws-slot={hasName ? "name" : settling ? "settling" : shimmering ? "loading" : "stalled"}
            className="block font-display font-semibold text-sm text-lm-text truncate leading-tight"
          >
            {hasName ? (
              current.name
            ) : settling ? (
              // No text at all. Width reserved so nothing jumps later.
              <span className="inline-block w-28 h-4 rounded bg-lm-tint align-middle" aria-hidden="true" />
            ) : shimmering ? (
              <span className="inline-block w-28 h-4 rounded ws-shimmer align-middle" aria-hidden="true" />
            ) : stalled ? (
              // Em-dash, not a skeleton. A skeleton promises an arrival.
              <span className="text-lm-text3" data-testid="workspace-switcher-name-stalled">—</span>
            ) : (
              <span className="inline-block w-28 h-4 rounded bg-lm-tint align-middle" aria-hidden="true" />
            )}
          </span>
          {/* Role caption only. The brand moved OUT to its own control beside
              this one — see BrandSwitcher.jsx. */}
          <span className="block text-[11px] text-lm-text3 truncate leading-tight capitalize">
            {current?.role ? `${current.role} · workspace` : stalled ? " " : "Workspace"}
          </span>
        </span>
        <ChevronsUpDown className={`w-3.5 h-3.5 text-lm-text3 shrink-0 ${open ? "opacity-100" : "opacity-50 group-hover:opacity-100"}`} />
      </button>

      {/* Portaled dropdown + backdrop — escapes the sticky-header stacking context */}
      {open && coords && createPortal(
        <>
          <div className="fixed inset-0 z-[90]" onPointerDown={() => setOpen(false)} aria-hidden />
          <div
            data-testid="workspace-switcher-menu"
            style={{ top: coords.top, left: coords.left }}
            className="fixed z-[100] w-80 max-w-[calc(100vw-1rem)] bg-lm-surface border border-lm-border rounded-xl shadow-[0_16px_40px_-12px_rgba(0,0,0,0.28)] overflow-hidden animate-[fadeIn_0.15s_ease]"
          >
            <div className="px-4 pt-3 pb-2">
              <div className="text-[11px] font-mono uppercase tracking-widest text-lm-text3">Switch workspace</div>
            </div>
            {items === null ? (
              <div className="flex items-center gap-2 text-lm-text2 text-sm px-4 py-5">
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="text-sm text-lm-text2 px-4 py-5">No workspaces found.</div>
            ) : (
              <ul className="max-h-80 overflow-y-auto px-1.5 pb-1.5">
                {items.map((ws) => {
                  const active = ws.is_active;
                  return (
                    <li key={ws.workspace_id}>
                      <button
                        onClick={() => switchTo(ws)}
                        disabled={switching !== null}
                        data-testid={`workspace-switcher-item-${ws.workspace_id}`}
                        className={`w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-left transition-colors disabled:opacity-70 ${
                          active ? "bg-lm-tint/70 ring-1 ring-lm-primary/20" : "hover:bg-lm-tint/40"
                        }`}
                      >
                        <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-lm-primary text-white" : "bg-lm-tint2 text-lm-primary"}`}>
                          {ws.role === "owner" ? <Crown className="w-4 h-4" /> : <span className="font-display font-bold text-sm">{(ws.name || "?").slice(0, 1).toUpperCase()}</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate text-lm-text">{ws.name}</div>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <span className={`text-[10px] font-mono uppercase tracking-widest border rounded px-1.5 py-0.5 ${ROLE_STYLES[ws.role] || ROLE_STYLES.member}`}>
                              {ws.role}
                            </span>
                            <span className="text-[11px] text-lm-text3 font-mono">
                              {ws.member_count} member{ws.member_count === 1 ? "" : "s"}
                            </span>
                          </div>
                        </div>
                        <div className="shrink-0">
                          {switching === ws.workspace_id ? (
                            <Loader2 className="w-4 h-4 animate-spin text-lm-text3" />
                          ) : active ? (
                            <Check className="w-4 h-4 text-lm-primary" />
                          ) : null}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}

            {/* Absent, not disabled, when the flag is off — see `showCreate`.
                Rendered outside the list branches on purpose: it does not depend
                on the list loading, and it is the only useful action in the
                "No workspaces found" case. */}
            {showCreate ? (
              <div className="border-t border-lm-border p-1.5">
                <button
                  type="button"
                  data-testid="workspace-switcher-create"
                  onClick={() => {
                    setOpen(false);
                    setCreating(true);
                  }}
                  disabled={switching !== null}
                  className="w-full flex items-center gap-3 px-2.5 py-2.5 rounded-lg text-left transition-colors hover:bg-lm-tint/40 disabled:opacity-70"
                >
                  <span className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-lm-tint2 text-lm-primary">
                    <Plus className="w-4 h-4" />
                  </span>
                  {/* COPY SLOT ws.create.action — an obvious button label. */}
                  <span className="text-sm font-medium text-lm-text">New workspace</span>
                </button>
              </div>
            ) : null}

          </div>
        </>,
        document.body
      )}

      <NewWorkspaceDialog
        open={creating}
        onClose={() => setCreating(false)}
        onFeatureDisabled={() => setFlagRevoked(true)}
      />
    </div>
  );
}
